/* @refresh reset */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";

import NavBar from "./components/NavBar";
import BACKEND_URL from "./config";
import { isAdmin, getToken } from "./auth/token";

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = Number(bytes);
  let idx = 0;
  while (v >= 1024 && idx < units.length - 1) {
    v = v / 1024;
    idx += 1;
  }
  return `${v.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function formatUpdatedAt(epochSecondsOrMs) {
  if (!epochSecondsOrMs) return "—";
  // backend uses os.path.getmtime -> seconds (float). JS Date expects ms.
  const ms =
    epochSecondsOrMs > 10_000_000_000 ? epochSecondsOrMs : epochSecondsOrMs * 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function defaultNewScriptTemplate() {
  return {
    version: 1,
    name: "New Interview Script",
    description: "Describe what this script is for.",
    phases: [
      {
        id: "phase_1",
        label: "Phase 1",
        description: "Describe the learning goal for this phase.",
        max_followups_per_core_question: 3,
        core_questions: [
          {
            id: "cq_1",
            prompt: "Tell me about…",
            goal: "What are you trying to learn?",
            notes_for_model: "Any special guidance for follow-ups.",
            completion_criteria: ["Participant gave a clear, concrete example."],
            refusal_policy: "If they refuse, acknowledge and move on.",
            max_followups: 3,
            require_step_confirmation: false,
            // Shows a "Show, don't tell" reminder under this question in the
            // participant UI. Omit or false for questions that aren't demoable.
            show_screen_reminder: false,
          },
        ],
      },
    ],
  };
}

function AdminInterviewScripts() {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [scripts, setScripts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Editor state
  const [mode, setMode] = useState("view"); // "view" | "create" | "edit"
  const [scriptIdInput, setScriptIdInput] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [parseError, setParseError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]); // [{path,message}]
  const [requestError, setRequestError] = useState("");

  const selectedSummary = useMemo(() => {
    return scripts.find((s) => s.script_id === selectedId) || null;
  }, [scripts, selectedId]);

  async function fetchScripts() {
    const resp = await fetch(`${BACKEND_URL}/api/admin/interview-scripts`, {
      headers: authHeaders(),
    });
    if (!resp.ok) {
      const t = await resp.text();
      throw new Error(t || `Failed to load scripts (${resp.status})`);
    }
    const data = await resp.json();
    setScripts(data.scripts || []);
  }

  async function loadScript(scriptId) {
    setRequestError("");
    setParseError("");
    setValidationErrors([]);

    const resp = await fetch(
      `${BACKEND_URL}/api/admin/interview-scripts/${encodeURIComponent(scriptId)}`,
      { headers: authHeaders() }
    );

    if (!resp.ok) {
      const t = await resp.text();
      throw new Error(t || `Failed to load script (${resp.status})`);
    }

    const data = await resp.json();
    const obj = data.script || {};
    const text = JSON.stringify(obj, null, 2);

    setSelectedId(scriptId);
    setMode("edit");
    setScriptIdInput(scriptId);
    setJsonText(text);
    setValidationErrors(data.validation_errors || []);
  }

  useEffect(() => {
    const admin = isAdmin();
    if (!admin) {
      navigate("/");
      return;
    }

    (async () => {
      try {
        setLoading(true);
        await fetchScripts();
      } catch (e) {
        setRequestError(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  function beginCreate() {
    setSelectedId(null);
    setMode("create");
    setScriptIdInput("");
    setParseError("");
    setValidationErrors([]);
    setRequestError("");
    setJsonText(JSON.stringify(defaultNewScriptTemplate(), null, 2));
  }

  function tryParseJsonOrSetError() {
    setParseError("");
    setValidationErrors([]);
    setRequestError("");

    try {
      const parsed = JSON.parse(jsonText);
      return parsed;
    } catch (e) {
      setParseError(`Invalid JSON: ${String(e?.message || e)}`);
      return null;
    }
  }

  async function onSave() {
    const scriptObj = tryParseJsonOrSetError();
    if (!scriptObj) return;

    const id = (scriptIdInput || "").trim();
    if (mode === "create" && !id) {
      setRequestError("Please enter a script_id (example: jtbd_workflow_v1).");
      return;
    }

    setSaving(true);
    setRequestError("");
    setValidationErrors([]);

    try {
      const url =
        mode === "create"
          ? `${BACKEND_URL}/api/admin/interview-scripts`
          : `${BACKEND_URL}/api/admin/interview-scripts/${encodeURIComponent(id)}`;

      const method = mode === "create" ? "POST" : "PATCH";

      const resp = await fetch(url, {
        method,
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(
          mode === "create" ? { script_id: id, script: scriptObj } : { script: scriptObj }
        ),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        if (data?.error === "validation_failed") {
          setValidationErrors(data.validation_errors || []);
          setRequestError("Schema validation failed. Fix the errors below and try again.");
          return;
        }
        setRequestError(data?.message || data?.error || `Save failed (${resp.status})`);
        return;
      }

      // Refresh list + reload current
      await fetchScripts();
      setMode("edit");
      setSelectedId(id);
      // Re-fetch to get server-side validation_errors (should be empty)
      await loadScript(id);
    } catch (e) {
      setRequestError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    const id = (scriptIdInput || "").trim();
    if (!id) return;

    // As requested: do not block deletion even if in use; we just warn
    const ok = window.confirm(
      `Delete script "${id}"?\n\nThis does NOT check whether interview types are using it (and deletion is allowed).`
    );
    if (!ok) return;

    setSaving(true);
    setRequestError("");
    setValidationErrors([]);
    setParseError("");

    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/admin/interview-scripts/${encodeURIComponent(id)}`,
        { method: "DELETE", headers: authHeaders() }
      );

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setRequestError(data?.message || data?.error || `Delete failed (${resp.status})`);
        return;
      }

      await fetchScripts();
      setSelectedId(null);
      setMode("view");
      setScriptIdInput("");
      setJsonText("");
    } catch (e) {
      setRequestError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  function onPrettyPrint() {
    const parsed = tryParseJsonOrSetError();
    if (!parsed) return;
    setJsonText(JSON.stringify(parsed, null, 2));
  }

  if (loading) {
    return (
      <>
        <NavBar />
        <Box sx={{ p: 3 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5">Admin: Interview Scripts</Typography>
          <Button variant="contained" onClick={beginCreate}>
            New Script
          </Button>
        </Stack>

        {requestError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {requestError}
          </Alert>
        ) : null}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {/* Left: list */}
          <Paper sx={{ width: { xs: "100%", md: 420 }, p: 1 }}>
            <Typography variant="subtitle1" sx={{ px: 1, py: 1 }}>
              Scripts
            </Typography>
            <Divider />
            <List dense>
              {scripts.map((s) => {
                const primary = `${s.name || "(no name)"}  —  ${s.script_id}`;
                const usedBy = (s.used_by_interview_types || []).map((t) => t?.name).filter(Boolean);
                const secondary = [
                  s.description ? s.description : null,
                  `Used by: ${s.usage_count || 0}${usedBy.length ? ` (${usedBy.join(", ")})` : ""}`,
                  `Updated: ${formatUpdatedAt(s.updated_at)} · Size: ${formatBytes(s.size_bytes)}`,
                ]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <ListItemButton
                    key={s.script_id}
                    selected={s.script_id === selectedId}
                    onClick={() => loadScript(s.script_id)}
                  >
                    <ListItemText primary={primary} secondary={secondary} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          {/* Right: editor */}
          <Paper sx={{ flex: 1, p: 2 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
                <TextField
                  label="script_id (filename)"
                  value={scriptIdInput}
                  onChange={(e) => setScriptIdInput(e.target.value)}
                  disabled={mode === "edit"} // cannot change id on edit (matches backend)
                  placeholder="example: product_specific"
                  sx={{ width: { xs: "100%", md: 360 } }}
                />

                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={onPrettyPrint} disabled={!jsonText || saving}>
                    Pretty-print JSON
                  </Button>
                  <Button variant="contained" onClick={onSave} disabled={saving || !jsonText}>
                    {saving ? "Saving…" : mode === "create" ? "Create" : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={onDelete}
                    disabled={saving || !scriptIdInput || mode === "create"}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>

              {selectedSummary ? (
                <Alert severity="info">
                  <b>Selected:</b> {selectedSummary.script_id} · <b>Used by:</b>{" "}
                  {selectedSummary.usage_count || 0}
                  {selectedSummary.used_by_interview_types?.length ? (
                    <>
                      {" "}
                      (
                      {selectedSummary.used_by_interview_types
                        .map((t) => t?.name)
                        .filter(Boolean)
                        .join(", ")}
                      )
                    </>
                  ) : null}
                </Alert>
              ) : null}

              {parseError ? <Alert severity="error">{parseError}</Alert> : null}

              {validationErrors && validationErrors.length ? (
                <Alert severity="warning">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Schema validation errors
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {validationErrors.map((e, idx) => (
                      <li key={idx}>
                        <code>{e.path}</code>: {e.message}
                      </li>
                    ))}
                  </Box>
                </Alert>
              ) : null}

              <TextField
                label="Script JSON"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                multiline
                minRows={24}
                maxRows={40}
                fullWidth
                spellCheck={false}
                placeholder='{"version": 1, "phases": [...]}'
              />

              <Typography variant="caption" color="text.secondary">
                Tip: extra keys are allowed; required keys/types must match your “mostly strict” backend
                validator.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Write <code>{"{{topic}}"}</code> anywhere in a prompt, criterion or note to have it
                replaced with the interview type's job / product name at run time — e.g.{" "}
                <em>“Where does {"{{topic}}"} feel hardest?”</em> — instead of a bare “this” the
                participant has to interpret.
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </>
  );
}

export default AdminInterviewScripts;
